import { z } from 'zod';
import { LangCodeSchema, TimestampSchema, UuidSchema } from './primitives';

// =============================================================================
// User profile extensions (users table additions from migration 0004)
// =============================================================================

export const ThemeSchema = z.enum(['system', 'light', 'dark']);
export type Theme = z.infer<typeof ThemeSchema>;

/**
 * Input schema for self-updating the shared `users` profile row.
 * Only user-level fields (not membership-level).
 */
export const UserProfileUpdateSchema = z
  .object({
    display_name: z.string().min(1).max(120).nullable(),
    avatar_url: z.string().url().nullable(),
    phone: z.string().max(40).nullable(),
    timezone: z.string().min(1).max(64),
    preferred_lang: LangCodeSchema,
    theme: ThemeSchema,
  })
  .partial();
export type UserProfileUpdate = z.infer<typeof UserProfileUpdateSchema>;

// =============================================================================
// MembershipProfile
// =============================================================================

export const MembershipStatusSchema = z.enum(['active', 'invited', 'suspended', 'terminated']);
export type MembershipStatus = z.infer<typeof MembershipStatusSchema>;

/** Row shape for `memberships_profile`. */
export const MembershipProfileSchema = z.object({
  tenant_id: UuidSchema,
  user_id: UuidSchema,
  title: z.string().max(200).nullable(),
  department: z.string().max(200).nullable(),
  manager_id: UuidSchema.nullable(),
  employee_number: z.string().max(100).nullable(),
  hired_at: z.string().date().nullable(),
  terminated_at: z.string().date().nullable(),
  bio: z.string().max(4000).nullable(),
  status: MembershipStatusSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type MembershipProfile = z.infer<typeof MembershipProfileSchema>;

/**
 * Input schema for PATCH /people/members/:id.
 * Admin/owner can update any field; the user themselves can only update bio
 * and title (enforced by RLS + Edge Function role check).
 */
export const MembershipProfileUpdateSchema = z
  .object({
    title: z.string().max(200).nullable(),
    department: z.string().max(200).nullable(),
    manager_id: UuidSchema.nullable(),
    employee_number: z.string().max(100).nullable(),
    hired_at: z.string().date().nullable(),
    terminated_at: z.string().date().nullable(),
    bio: z.string().max(4000).nullable(),
    status: MembershipStatusSchema,
  })
  .partial();
export type MembershipProfileUpdate = z.infer<typeof MembershipProfileUpdateSchema>;

// =============================================================================
// Teams
// =============================================================================

export const TeamRoleSchema = z.enum(['member', 'lead']);
export type TeamRole = z.infer<typeof TeamRoleSchema>;

/** Row shape for `teams`. */
export const TeamSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  slug: z
    .string()
    .min(2)
    .max(63)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).nullable(),
  parent_team_id: UuidSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Team = z.infer<typeof TeamSchema>;

/** Input for POST /people/teams. */
export const TeamCreateInputSchema = z.object({
  slug: z
    .string()
    .min(2)
    .max(63)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  parent_team_id: UuidSchema.nullable().optional(),
});
export type TeamCreateInput = z.infer<typeof TeamCreateInputSchema>;

/** Input for PATCH /people/teams/:id. */
export const TeamUpdateInputSchema = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable(),
    parent_team_id: UuidSchema.nullable(),
  })
  .partial();
export type TeamUpdateInput = z.infer<typeof TeamUpdateInputSchema>;

/** Row shape for `team_members`. */
export const TeamMemberSchema = z.object({
  tenant_id: UuidSchema,
  team_id: UuidSchema,
  user_id: UuidSchema,
  role: TeamRoleSchema,
  joined_at: TimestampSchema,
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

/** Input for POST /people/teams/:id/members. */
export const TeamMemberAddInputSchema = z.object({
  user_id: UuidSchema,
  role: TeamRoleSchema.default('member'),
});
export type TeamMemberAddInput = z.infer<typeof TeamMemberAddInputSchema>;

// =============================================================================
// Paginated list response envelope (shared by members + teams lists)
// =============================================================================

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    data: z.array(itemSchema),
    total: z.number().int().nonnegative(),
    cursor: z.string().nullable(),
  });
